/**
 * Sequential TTS playback for bot answers (live voice answers and message replay).
 *
 * The server streams one base64 mp3 per sentence over WS (`stream_audio`, then
 * `end_stream_audio`). Segments are self-contained: play one, then the next — no
 * MediaSource, no stitching. A single reused HTMLAudioElement keeps the browser's
 * autoplay grant (obtained via unlock() inside a user gesture) valid for the whole queue.
 *
 * One voice at a time: only the message armed via expect() may enqueue and play.
 * Arming a new message silences the previous one for good — its late WS segments are
 * ignored by construction (no per-message stopped bookkeeping needed).
 *
 * State lives here (not in redux: the store is persisted, playback state must not survive
 * a reload); components subscribe via useSyncExternalStore in hooks/useAudioPlayback.ts.
 */

type Segment = { seq: number; base64: string }

type QueueState = {
  segments: Map<number, Segment>
  nextSeq: number
  // Fallback ordering for events without seq (message replay): append in arrival order.
  fallbackSeq: number
  ended: boolean // end_stream_audio received
}

const queues = new Map<string, QueueState>()
const listeners = new Set<() => void>()

let audioEl: HTMLAudioElement | null = null
let playingMessageId: string | null = null
let currentUrl: string | null = null
// The only message allowed to play: the answer to the last voice question, or the last
// replayed message. null = nothing may play (text questions produce no audio anyway).
let expectedMessageId: string | null = null

// ~100ms of silence: real playback inside the user gesture is what earns the autoplay grant.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA='

// TTS at natural speed feels slow in a chat; browsers pitch-correct by default (preservesPitch).
const PLAYBACK_RATE = 1.15

const getAudioEl = () => {
  if (!audioEl) {
    audioEl = new Audio()
    audioEl.addEventListener('ended', onSegmentDone)
    audioEl.addEventListener('error', onSegmentDone)
  }
  return audioEl
}

const notify = () => listeners.forEach((listener) => listener())

const base64ToUrl = (base64: string) => {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
  return URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }))
}

const releaseUrl = () => {
  if (currentUrl) {
    URL.revokeObjectURL(currentUrl)
    currentUrl = null
  }
}

/** Silence everything: clear all queues and stop the element. Leaves expectedMessageId alone. */
const haltAll = () => {
  queues.clear()
  if (playingMessageId) {
    releaseUrl()
    const el = getAudioEl()
    el.pause()
    el.removeAttribute('src')
    playingMessageId = null
    notify()
  }
}

const cleanupQueue = (messageId: string) => {
  queues.delete(messageId)
  // cleanupQueue is terminal for the message (finished, stopped or errored): un-expect it
  // so subscribers see "not loading, not playing".
  if (expectedMessageId === messageId) expectedMessageId = null
  if (playingMessageId === messageId) {
    releaseUrl()
    const el = getAudioEl()
    el.pause()
    el.removeAttribute('src')
    playingMessageId = null
  }
  notify()
}

const playNext = (messageId: string) => {
  const queue = queues.get(messageId)
  if (!queue) return
  const segment = queue.segments.get(queue.nextSeq)
  if (!segment) {
    // Nothing buffered at the expected seq: done if the stream ended, otherwise wait.
    if (queue.ended) cleanupQueue(messageId)
    return
  }
  queue.segments.delete(queue.nextSeq)
  queue.nextSeq += 1
  releaseUrl()
  currentUrl = base64ToUrl(segment.base64)
  const el = getAudioEl()
  el.muted = false // unlock() plays its silent clip muted; real segments must be audible
  el.src = currentUrl
  el.playbackRate = PLAYBACK_RATE // after src: loading can reset the rate on some browsers
  if (playingMessageId !== messageId) {
    playingMessageId = messageId
    notify()
  }
  el.play().catch(() => cleanupQueue(messageId)) // autoplay denied or bad segment: fail silently, text is there
}

function onSegmentDone() {
  if (playingMessageId) playNext(playingMessageId)
}

export const audioPlayer = {
  /** Call synchronously inside a user gesture (mic stop tap, listen tap): actually playing
   *  a silent clip (not a src-less play(), which rejects and may not register the grant —
   *  Safari) earns this element the autoplay grant for the WS-driven play() that follows. */
  unlock() {
    const el = getAudioEl()
    el.muted = true
    el.src = SILENT_WAV
    el.play().catch(() => undefined)
  },

  /** Arm playback for one message (the answerId of a voice question, or a replayed message).
   *  One voice at a time: whatever was talking stops for good. */
  expect(messageId: string) {
    if (expectedMessageId !== messageId) haltAll()
    expectedMessageId = messageId
    notify()
  },

  enqueue(messageId: string, seq: number | undefined, base64: string) {
    if (messageId !== expectedMessageId) return
    let queue = queues.get(messageId)
    if (!queue) {
      queue = { segments: new Map(), nextSeq: 0, fallbackSeq: 0, ended: false }
      queues.set(messageId, queue)
    }
    const effectiveSeq = seq ?? queue.fallbackSeq++
    queue.segments.set(effectiveSeq, { seq: effectiveSeq, base64 })
    // Idle (nothing playing, or this queue is the active one but between segments): kick it.
    if (playingMessageId === null || (playingMessageId === messageId && getAudioEl().paused)) {
      playNext(messageId)
    }
  },

  endOfStream(messageId: string) {
    const queue = queues.get(messageId)
    if (!queue) {
      // TTS failed server-side (no segments) or already stopped: nothing will ever play.
      if (expectedMessageId === messageId) {
        expectedMessageId = null
        notify()
      }
      return
    }
    queue.ended = true
    // If we were waiting on a segment that will never come, close out now.
    if (playingMessageId === messageId && getAudioEl().paused) playNext(messageId)
    if (playingMessageId !== messageId && queue.segments.size === 0) queues.delete(messageId)
  },

  stop(messageId: string) {
    cleanupQueue(messageId) // also un-expects it
  },

  /** New recording or new outgoing message interrupts whatever is talking. */
  stopAll() {
    expectedMessageId = null
    haltAll()
    notify()
  },

  // useSyncExternalStore contract
  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  getPlayingMessageId: () => playingMessageId,
  getExpectedMessageId: () => expectedMessageId,
}
